/* "איך זה חושב?": כל מספר במסך לחיץ, ופותח את ההסבר שלו.
   כל הסבר מחושב מחדש מאותן פונקציות שמציגות את המספר, ומחזיר נתונים בלבד:
   { t: כותרת, v: המספר, d: מה הוא אומר, rows: החישוב שורה אחר שורה,
     list/lt, list2/lt2: הפעולות שנכנסו אליו, note: הערה, actions: כפתורים, kind: הסבר מותאם } */
import { S } from './state.js';
import { fMoney, wD, wDY, wDYY, signed, todayISO, fISO, absAmt } from '../lib/format.js';
import { curKey, keyAdd, periodStart, periodEnd, periodShort, keyOfISO } from './periods.js';
import { getTxSrc, filterActive } from './tx.js';
import { bankFreshness, balTodayParts, balSinceAnchor, balEst, balToday, periodOpen, periodFlow, periodOpening } from './balance.js';
import { strictMode, project, projToEnd, futurePeriodForecast, monthlyParts, phraseForDays, rangeEnd, rangePhrase, STRESS, recInRange } from './forecast.js';
import { cardLateCharge, cardChargeEvents, cardChargeAmount, cardFutureEstimate, cardEstimateFor, cardPeriodSums, cardClosedKeys, cardPeriodCredit, cardPace, cardTemplate, cardCurKey } from './card.js';
import { exitPlan, safeToSpend, paceParts, savPeriod, bufSuggest } from './plan.js';
import { realisticToEnd } from './afford.js';
import { savGoalStats, savFrom, savMonthsTo, savUnassigned, isSavingRec } from './savings.js';
import { recEvery, recNow, recEst } from './recurring.js';

/** פעולה כשורת פירוט */
export function wTx(t) {
  const inc = t.type === 'income', a = absAmt(t);
  return { d: wD(t.date), n: t.description || 'ללא תיאור', v: inc ? a : -a, s: getTxSrc(t) === 'credit' ? 'אשראי' : 'עו"ש' };
}
const byAmt = (a, b) => a.v - b.v;
const sum = (L, f) => L.reduce((s, x) => s + f(x), 0);

/* ── תחזית עד סוף התקופה, כרשימה ── */
function projItems(until, from, noLate) {
  const out = [];
  projToEnd().forEach((x) => {
    if (from && x.date < from) return;
    if (x.date > until) return;
    x.events.forEach((v) => {
      if (noLate && v.late) return; // כבר בתוך היתרה של היום
      out.push({
        d: wD(x.date), n: v.name, v: v.type === 'income' ? v.amount : -v.amount,
        s: v.card ? (v.estimated ? 'הערכה לפי תקופות קודמות' : 'סכום האשראי בתקופה') : v.real ? 'כבר רשום' : v.late ? 'מועד שעבר ועוד לא נקלט' : v.type === 'income' ? 'הכנסה קבועה' : 'חיוב קבוע',
      });
    });
  });
  return out;
}
const PROJ_NOTE = 'התחזית מתחילה מ"בחשבון היום" ומוסיפה רק מה שידוע מראש: חיובים קבועים והכנסות קבועות (מההגדרות), ' +
  'וחיוב כרטיס האשראי. חיוב הכרטיס הוא סך עסקאות האשראי שנרשמו בתקופה, או הסכום שהזנת ידנית. ' +
  'הוצאות שעוד לא קרו בתקופה הזו, כמו סופר או דלק, לא בתחזית. בשבילן יש "מותר להוציא היום".';
/* בתקופות הבאות יש גם הערכה להוצאות שוטפות. אותו משפט בכל ההסברים של מסך התזרים */
const FLOW_NOTE_CUR = 'בתקופה הנוכחית: רק מה שידוע (בלי סופר, דלק וכו\', בשבילם יש "מותר להוציא היום"). ';
const flowNote = () => FLOW_NOTE_CUR + (strictMode()
  ? 'בתקופות הבאות (מצב מחמיר): גם הערכה להוצאות השוטפות, בעו"ש כשורה שבועית ובאשראי בתוך הערכת חיוב הכרטיס.'
  : 'בתקופות הבאות (מצב רגיל): חיוב הכרטיס מוערך לפי תקופות קודמות, והוצאות שוטפות מהעו"ש (ביט, מזומן) לא נכללות.');
const pe = () => periodEnd(curKey());
const toSettings = { label: '🏦 עדכון יתרת הפתיחה בהגדרות', nav: 'Set' };

const W = {};

/** "בחשבון היום" כשאין נתוני עו"ש יותר מ-10 ימים: זו הערכה, ולא היתרה בבנק */
function staleNote() {
  const F = bankFreshness();
  return F.stale ? 'שימו לב: אין נתוני עו"ש מאז ' + wD(F.last) + ' (' + F.days + ' ימים). המספר הוא הערכה: היתרה של אז, ועוד חיובים והכנסות קבועים שהנחנו שקרו לפי התאריך שלהם. כדי שיהיה מדויק, סרקו את העו"ש או עדכנו את היתרה. ' : '';
}
W.bal = () => {
  const Q = balTodayParts();
  if (Q) {
    const rows = [
      { k: 'פתיחת התקופה (' + wD(Q.ps) + ') = סגירת התקופה הקודמת', v: Q.open },
      { k: 'הכנסות בעו"ש בתקופה (' + Q.ni + ')', v: Q.inc, op: '+' },
      { k: 'הוצאות בעו"ש בתקופה (' + Q.ne + ')', v: Q.exp, op: '-' },
    ];
    Q.cards.forEach((c) => rows.push({ k: 'חיוב כרטיס אשראי (' + wD(c.iso) + ')', v: c.amount, op: '-' }));
    Q.late.forEach((x) => rows.push({ k: x.name + ' · ' + wD(x.iso) + ' (מועד שעבר, עוד לא נסרק)', v: Math.abs(x.amount), op: x.amount < 0 ? '-' : '+' }));
    rows.push({ k: 'יתרה היום', v: Q.amount, tot: 1 });
    return {
      t: 'יתרה היום בעו"ש', v: Q.amount,
      d: staleNote() + 'היתרה מחושבת מפתיחת התקופה, שהיא בדיוק הסגירה של התקופה הקודמת, ועוד תנועות העו"ש ששייכות לתקופה הזו.' + (Q.late.length ? ' נוספים גם חיובים והכנסות קבועים שהתאריך שלהם כבר עבר ועוד לא נסרקו.' : ''),
      rows, list: Q.txs.map(wTx), lt: 'התנועות בעו"ש בתקופה',
      note: 'עסקאות אשראי לא מורידות מהיתרה ביום הקנייה. הן יורדות מהעו"ש יחד, ביום חיוב הכרטיס. כל תנועת עו"ש נספרת בתקופה ששייכת אותה בסריקה. ' +
        'אם לא סרקת את העו"ש כמה ימים, היתרה כאן ישנה. כדאי לסרוק, או לעדכן את יתרת הפתיחה בהגדרות.',
      actions: [toSettings],
    };
  }
  const r = balSinceAnchor(), E = balEst();
  const rows = [
    { k: 'יתרת פתיחה מההגדרות (' + wD(r.b.from) + (S.set.balEod ? ', סוף היום' : ', תחילת היום') + ')', v: r.b.base },
    { k: 'הכנסות בעו"ש שנסרקו מאז (' + r.ni + ')', v: r.inc, op: '+' },
    { k: 'הוצאות בעו"ש שנסרקו מאז (' + r.ne + ')', v: r.exp, op: '-' },
  ];
  E.late.forEach((x) => rows.push({ k: x.name + ' · ' + wD(x.iso) + ' (מועד שעבר, עוד לא נסרק)', v: Math.abs(x.amount), op: x.amount < 0 ? '-' : '+' }));
  rows.push({ k: 'יתרה היום', v: E.amount, tot: 1 });
  return {
    t: 'יתרה היום בעו"ש', v: E.amount,
    d: staleNote() + 'היתרה מחושבת מיתרת הפתיחה שבהגדרות, ועוד כל התנועות בעו"ש שנסרקו מאז.' + (E.late.length ? ' נוספים גם חיובים והכנסות קבועים שהתאריך שלהם כבר עבר ועוד לא נסרקו, בהנחה שהם קרו.' : ''),
    rows, list: r.txs.map(wTx), lt: 'התנועות בעו"ש מאז',
    note: 'עסקאות אשראי לא מורידות מהיתרה ביום הקנייה. הן יורדות מהעו"ש יחד, ביום חיוב הכרטיס. ' +
      'אם לא סרקת את העו"ש כמה ימים, היתרה כאן ישנה. כדאי לסרוק, או לעדכן את יתרת הפתיחה בהגדרות.',
    actions: [toSettings],
  };
};

/* המספרים הראשיים הם "רק מה שידוע", נכון לרגע זה. לידם, בחלון ההסבר, המספר המציאותי: אם תוציאו כרגיל */
const NOW_D = 'נכון לרגע זה: אם מהיום לא תוציאו כלום מעבר למה שכבר ידוע. המספר ישתנה עם כל קנייה ועם כל סריקה.';
function realBlock(which) {
  const R = realisticToEnd();
  if (!R || !R.extra.length) return { note2: ' כשיהיו סרוקות 2 תקופות קודמות (עו"ש ואשראי), יופיע כאן גם מספר מציאותי: מה יהיה אם תוציאו כרגיל.' };
  const rt2 = which === 'low' ? 'אם תוציאו כרגיל' : 'אם תוציאו כרגיל עד ' + wD(pe());
  const d2 = 'הערכה לפי החציון של 3 התקופות הקודמות. בחודש חריג (חג, תיקון ברכב) המספר האמיתי יהיה אחר.';
  if (which === 'low' && R.lowKnown - R.low < 1) return { rt2, d2: 'עד היום הכי נמוך לא צפויות הוצאות רגילות שיורדות מהעו"ש, ולכן גם אם תוציאו כרגיל הוא יהיה ' + fMoney(R.low) + '. ההוצאות הרגילות ירדו אחריו (למשל בחיוב הכרטיס), ומשפיעות על סוף התקופה.' };
  if (which === 'low') return { rt2, d2, rows2: [
    { k: 'הכי נמוך לפי מה שידוע', v: R.lowKnown },
    { k: 'הוצאות רגילות שעוד יגיעו עד אז', v: R.lowKnown - R.low, op: '-' },
    { k: 'הכי נמוך אם תוציאו כרגיל (' + wD(R.lowDate) + ')', v: R.low, tot: 1 },
  ] };
  return { rt2, d2, rows2: [
    { k: 'בסוף התקופה לפי מה שידוע', v: R.endKnown },
    ...(R.cash ? [{ k: 'הוצאות שוטפות מהעו"ש (ביט, מזומן, חד-פעמיות)', v: R.cash, op: '-' }] : []),
    ...(R.card ? [{ k: 'קניות רגילות שעוד יגיעו לחיוב הכרטיס', v: R.card, op: '-' }] : []),
    { k: 'בסוף התקופה אם תוציאו כרגיל', v: R.end, tot: 1 },
  ] };
}

W.end = () => {
  const P = exitPlan(), now = balEst().amount, L = projItems(pe(), null, true), RB = realBlock('end');
  return {
    t: 'בסוף התקופה (' + wD(pe()) + ')', v: P.end, ...RB,
    d: 'היתרה הצפויה בעו"ש ביום האחרון של התקופה. ' + NOW_D,
    rows: [
      { k: 'בחשבון היום', v: now },
      { k: 'הכנסות צפויות עד ' + wD(pe()), v: sum(L.filter((x) => x.v > 0), (x) => x.v), op: '+' },
      { k: 'חיובים צפויים עד ' + wD(pe()), v: sum(L.filter((x) => x.v < 0), (x) => x.v), op: '-' },
      { k: 'בסוף התקופה', v: P.end, tot: 1 },
    ],
    list: L, lt: 'מה צפוי עד סוף התקופה', note: PROJ_NOTE + (RB.note2 || ''),
  };
};

W.low = () => {
  const P = exitPlan(), now = balEst().amount, L = projItems(P.lowDate, null, true), RB = realBlock('low');
  return {
    t: 'הכי נמוך (' + wD(P.lowDate) + ')', v: P.low, ...RB,
    d: 'היתרה הכי נמוכה שהעו"ש יגיע אליה עד סוף התקופה, בדרך כלל לפני כניסת המשכורת. ' + NOW_D,
    rows: [{ k: 'בחשבון היום', v: now }].concat(L.map((x) => ({ k: x.d + ' · ' + x.n, v: x.v, op: x.v < 0 ? '-' : '+' })))
      .concat([{ k: 'הכי נמוך, ב-' + wD(P.lowDate), v: P.low, tot: 1 }]),
    note: 'אחרי ' + wD(P.lowDate) + ' היתרה עולה שוב. ' + PROJ_NOTE + (RB.note2 || ''),
  };
};

W.rise = () => {
  const P = exitPlan(), now = balEst().amount;
  return {
    t: 'השינוי עד סוף התקופה', v: P.end - now, vs: 1,
    d: 'ההפרש בין היתרה היום ליתרה הצפויה בסוף התקופה.',
    rows: [{ k: 'יתרה היום', v: now }, { k: 'בסוף התקופה', v: P.end }, { k: 'שינוי (סוף התקופה פחות היום)', txt: signed(P.end - now), tot: 1 }],
    note: 'עלייה פירושה שעד סוף התקופה ייכנס יותר ממה שידוע שייצא. הוצאות שוטפות שעוד לא קרו יקטינו את העלייה.',
  };
};

W.limit = () => {
  const lim = Number(S.set.odLimit) || 0, now = balEst().amount, P = exitPlan();
  return {
    t: 'ניצול המסגרת', v: null,
    d: 'איזה חלק ממסגרת האשראי בעו"ש (' + fMoney(lim) + ') בשימוש.',
    rows: [
      { k: 'מינוס היום', v: Math.min(0, now) }, { k: 'המסגרת', v: lim, op: '÷' },
      { k: 'ניצול היום', txt: Math.round((Math.max(0, -now) / lim) * 100) + '%', tot: 1 },
      { k: 'מינוס בשפל (' + wD(P.lowDate) + ')', v: Math.min(0, P.low) }, { k: 'המסגרת', v: lim, op: '÷' },
      { k: 'ניצול בשפל', txt: Math.round((Math.max(0, -P.low) / lim) * 100) + '%', tot: 1 },
    ],
    note: 'את המסגרת משנים במסך התזרים. מעבר ל-100% הבנק עלול לסרב לחיובים.',
  };
};

W.status = () => {
  const lim = Number(S.set.odLimit) || 0, P = exitPlan();
  return {
    t: 'מה אומר הסימון', v: null,
    d: 'הסימון נקבע לפי השפל, היתרה הכי נמוכה עד סוף התקופה (' + fMoney(P.low) + ').',
    rows: [
      { k: '✓ בטוח', txt: 'השפל מעל אפס' },
      { k: 'במינוס', txt: 'השפל מתחת לאפס' + (lim ? ', עד 80% מהמסגרת' : '') },
      { k: 'קרוב למסגרת', txt: lim ? 'השפל בין 80% ל-100% מהמסגרת' : '-' },
      { k: 'חריגה מהמסגרת', txt: lim ? 'השפל מעבר למסגרת' : '-' },
    ],
    note: lim ? '' : 'לא הוגדרה מסגרת, ולכן כל שפל מתחת לאפס מסומן כאזהרה. אפשר להגדיר מסגרת במסך התזרים.',
  };
};

W.open = () => {
  const k = curKey(), ps = periodStart(k), pk = keyAdd(k, -1);
  const op = periodOpening(), pps = periodStart(pk), BF = periodFlow(pk), po = periodOpen(pk);
  const rows = [
    { k: 'פתיחת התקופה הקודמת (' + wD(pps) + ')', v: po },
    { k: 'נכנס לעו"ש בתקופה הקודמת', v: BF.inc, op: '+' },
    { k: 'יצא מהעו"ש בתקופה הקודמת' + (BF.card ? ' (בלי חיוב הכרטיס)' : ''), v: BF.exp, op: '-' },
  ];
  if (BF.card) rows.push({ k: cardChargeTxt(BF), v: BF.card, op: '-' });
  if (BF.late) rows.push({ k: 'קרה ועוד לא נסרק (למשל חיוב הכרטיס)', v: Math.abs(BF.late), op: BF.late < 0 ? '-' : '+' });
  rows.push({ k: 'סגירת התקופה הקודמת = פתיחת התקופה (' + wD(ps) + ')', v: op, tot: 1 });
  return {
    t: 'פתיחת התקופה (' + wD(ps) + ')', v: op,
    d: 'היתרה שבה התקופה מתחילה. זה בדיוק "יתרה בעו"ש בסוף התקופה" של התקופה הקודמת.',
    rows,
    note: 'כל תנועת עו"ש נספרת בתקופה שאליה שייכת אותה בסריקה. עסקאות אשראי לא נכללות לפי יום הקנייה, אלא כחיוב אחד של הכרטיס.',
  };
};

W.save = () => {
  const P = exitPlan(), t0 = new Date(todayISO() + 'T12:00:00'), t = new Date(S.set.exitTarget + 'T12:00:00');
  const days = Math.round((t - t0) / 86400000);
  return {
    t: 'חיסכון חודשי לפי התוכנית', v: P.required,
    d: 'כמה צריך לחסוך בכל חודש כדי ' + (P.stage === 1 ? 'לסגור את המינוס של סוף התקופה' : P.stage === 2 ? 'לסגור את המינוס של השפל' : 'לצבור את כרית הביטחון') + ' עד ' + wDYY(t) + '.',
    rows: [
      { k: P.stage === 1 ? 'המינוס בסוף התקופה' : P.stage === 2 ? 'המינוס בשפל (' + wD(P.lowDate) + ')' : 'חסר לכרית', v: P.need },
      { k: 'חודשים עד היעד (' + days + ' ימים ÷ 30.4)', txt: String(Math.round(P.months * 10) / 10), op: '÷' },
      { k: 'לחסוך בחודש', v: P.required, tot: 1 },
    ],
    note: '"לחסוך" פירושו לסיים כל תקופה ביתרה גבוהה יותר ממה שהתחלת אותה, בסכום הזה. ' +
      (P.stage === 2 ? 'כך השפל עולה בכל חודש באותו סכום, עד שהוא מגיע לאפס.' : 'כך סוף התקופה עולה בכל חודש, עד שהוא מגיע לאפס.'),
  };
};

W.need = () => {
  const P = exitPlan();
  if (P.stage === 3) return {
    t: 'כמה נשאר לצבור לכרית', v: P.need,
    d: 'שלב 3: אתה כבר לא במינוס, גם בשפל. כדי שגם ביום הכי נמוך יישארו בעו"ש ' + fMoney(P.buf) + ', צריך לצבור את ההפרש.',
    rows: [{ k: 'הכרית', v: P.buf }, { k: 'השפל (' + wD(P.lowDate) + ')', v: P.low, op: '-' }, { k: 'נשאר לצבור', v: P.need, tot: 1 }],
  };
  return P.stage === 1
    ? { t: 'כמה נשאר לסגור', v: P.debt, d: 'שלב 1: היתרה הצפויה בסוף התקופה עדיין במינוס. זה הסכום שצריך לסגור.', rows: [{ k: 'בסוף התקופה (' + wD(P.endDate) + ')', v: P.end }, { k: 'נשאר לסגור', v: P.debt, tot: 1 }] }
    : { t: 'כמה נשאר לצבור', v: P.need, d: 'שלב 2: בסוף התקופה אתה כבר בפלוס, אבל באמצע התקופה, לפני המשכורת, יורד למינוס. כדי שגם אז תהיה ביתרה חיובית, צריך לצבור כרית בגודל השפל.', rows: [{ k: 'השפל (' + wD(P.lowDate) + ')', v: P.low }, { k: 'נשאר לצבור', v: P.need, tot: 1 }] };
};

W.goal = () => {
  const s = safeToSpend();
  return {
    t: 'יעד לסוף התקופה', v: s.goal,
    d: s.saveAuto ? 'אתם במינוס ואין תאריך יעד, ולכן היעד הוא לסיים את התקופה עם מינוס קטן יותר, בגובה העודף של חודש רגיל.'
      : s.save ? (s.stage === 4 ? 'היתרה שצריך לסיים בה את התקופה כדי שיישאר מספיק להעביר לחיסכון את מה שעוד לא הועבר החודש.' : 'היתרה שצריך לסיים בה את התקופה כדי לעמוד בתוכנית.')
      : 'אין תוכנית יציאה עם יעד, ולכן היעד הוא לא לסיים את התקופה גרוע ממה שהתחלת אותה.',
    rows: [{ k: 'פתיחת התקופה', v: s.open }, { k: saveLabel(s), v: s.save, op: '+' }, { k: 'יעד לסוף התקופה', v: s.goal, tot: 1 }],
    note: s.stage === 4 ? 'העברות לחיסכון מורידות מהעו"ש, ולכן מה שכבר הועבר או מתוכנן כהוראת קבע לא נדרש שוב ביתרה.' : '',
  };
};

export function saveLabel(s) {
  return s.saveAuto ? 'צמצום המינוס (העודף החודשי)' : s.stage === 4 ? 'חיסכון שעוד לא הועבר החודש' : 'חיסכון לפי התוכנית';
}
W.save_auto = () => {
  const s = safeToSpend(), P = exitPlan();
  return {
    t: 'צמצום המינוס בתקופה', v: s.save,
    d: 'בלי תאריך יעד, כל תקופה צריכה להסתיים עם מינוס קטן יותר בגובה העודף של חודש רגיל. כך המינוס נסגר בקצב שהתקציב מאפשר.',
    rows: [{ k: 'עודף בחודש רגיל', v: P.surplus }, { k: 'נשאר לסגור', v: P.need }, { k: 'לצמצם בתקופה הזו (הקטן מביניהם)', v: s.save, tot: 1 }],
    note: 'אפשר לקבוע תאריך יעד בתוכנית היציאה מהמינוס, ואז הסכום יחושב לפיו.',
  };
};

W.left = () => {
  const s = safeToSpend();
  return {
    t: s.left > 0 ? 'נשאר להוצאות שוטפות' : 'חסר כדי להגיע ליעד', v: s.left,
    d: 'ההפרש בין מה שהתחזית אומרת שיהיה בסוף התקופה לבין היעד. זה הכסף שאפשר להוציא על דברים שלא בתחזית, ועדיין לעמוד ביעד.',
    rows: [{ k: 'צפי לסוף התקופה', v: s.end }, { k: 'היעד', v: s.goal }, { k: 'נשאר (הצפי פחות היעד)', v: s.left, tot: 1 }],
    note: 'הוצאות שוטפות: כל מה שלא חיוב קבוע, כמו סופר, דלק, מסעדות וקניות. אם תקנה באשראי, זה ייכנס לחיוב הכרטיס של התקופה ויוריד מהצפי.',
  };
};

W.perday = () => {
  const s = safeToSpend(), pd = Math.max(0, Math.floor(s.perDay / 10) * 10);
  return {
    t: 'אפשר להוציא ביום', v: pd,
    d: 'הסכום שנשאר להוצאות שוטפות, מחולק שווה בשווה בין הימים עד סוף התקופה.',
    rows: [{ k: 'נשאר להוצאות שוטפות', v: s.left }, { k: 'ימים עד ' + wD(s.endDate) + ', כולל היום', txt: String(s.days), op: '÷' }, { k: 'ביום', v: s.perDay }, { k: 'מעוגל כלפי מטה לעשרות', v: pd, tot: 1 }],
    note: 'אם תוציא היום פחות, המחר יגדל. אם יותר, המחר יקטן. המספר מתעדכן בכל סריקה.',
  };
};

W.pace = () => {
  const c = paceParts();
  return {
    t: 'הוצאות שוטפות עד עכשיו, ביום', v: c.v / c.days,
    d: 'כמה יצא בממוצע ביום מתחילת התקופה, בלי חיובים קבועים. מטרתו להשוות לסכום "אפשר להוציא ביום".',
    rows: [
      { k: 'כל ההוצאות בתקופה', v: c.all }, { k: 'חיובים קבועים (' + c.fxL.length + ')', v: c.fx, op: '-' },
      { k: 'פעולות מוחרגות (' + c.exL.length + ')', v: c.ex, op: '-' }, { k: 'הוצאות שוטפות', v: c.v, tot: 1 },
      { k: 'ימים מ-' + wD(c.ps) + ' עד היום, כולל', txt: String(c.days), op: '÷' }, { k: 'ביום', v: c.v / c.days, tot: 1 },
    ],
    list: c.vL.slice(0, 15).map(wTx), lt: 'ההוצאות השוטפות הגדולות',
    list2: c.fxL.map(wTx), lt2: 'חיובים קבועים שלא נספרו',
    note: 'חיוב נחשב קבוע אם השם שלו תואם חיוב קבוע בהגדרות. אם ברשימה למעלה יש הוצאה חד פעמית גדולה, הקצב האמיתי נמוך יותר.',
  };
};

function srcSplit(L) {
  let bank = 0, cred = 0;
  L.forEach((t) => { const a = absAmt(t); if (getTxSrc(t) === 'credit') cred += a; else bank += a; });
  return { bank, cred };
}
W.pinc = () => {
  const k = curKey(), L = filterActive(S.tx.filter((t) => t.month === k && t.type === 'income')), { bank, cred } = srcSplit(L);
  const fut = projItems(pe()).filter((x) => x.v > 0);
  return {
    t: 'הכנסות בתקופה עד עכשיו', v: bank + cred,
    d: 'סכום ההכנסות שכבר נרשמו בתקופה הזו, מתחילת התקופה ועד היום.',
    rows: [{ k: 'בעו"ש', v: bank }, { k: 'זיכויים באשראי', v: cred, op: '+' }, { k: 'סה"כ', v: bank + cred, tot: 1 }],
    list: L.map(wTx), lt: 'ההכנסות שנרשמו', list2: fut, lt2: 'עוד צפוי להיכנס עד ' + wD(pe()),
    note: fut.length ? 'ההכנסות שעוד לא נכנסו כבר כלולות בצפי לסוף התקופה.' : '',
  };
};
W.pexp = () => {
  const k = curKey(), L = filterActive(S.tx.filter((t) => t.month === k && t.type === 'expense')), { bank, cred } = srcSplit(L);
  const c = paceParts();
  return {
    t: 'הוצאות בתקופה עד עכשיו', v: bank + cred,
    d: 'סכום כל ההוצאות שכבר נרשמו בתקופה, גם באשראי וגם בעו"ש. הוצאה נספרת לפי יום הקנייה, לא לפי יום החיוב.',
    rows: [{ k: 'באשראי', v: cred }, { k: 'בעו"ש', v: bank, op: '+' }, { k: 'סה"כ', v: bank + cred, tot: 1 }],
    list: L.map(wTx).sort(byAmt).slice(0, 15), lt: 'ההוצאות הגדולות',
    note: (c.ex ? 'פעולות מוחרגות (' + fMoney(c.ex) + ') לא נספרות. ' : '') + 'עסקאות האשראי של התקופה יורדות מהעו"ש יחד, ביום חיוב הכרטיס.',
  };
};
W.usual = () => {
  const s = safeToSpend(), u = s.usualRest;
  return {
    t: 'הוצאה רגילה בשאר התקופה, ביום', v: u / s.days,
    d: 'כמה יצא בתקופות הקודמות, מאותו יום בתקופה ועד סופה, על הוצאות שאינן חיובים קבועים.',
    rows: [{ k: 'חציון של 3 התקופות הקודמות, מהיום עד סוף התקופה', v: u }, { k: 'ימים שנשארו', txt: String(s.days), op: '÷' }, { k: 'ביום', v: u / s.days, tot: 1 }],
    note: 'חציון ולא ממוצע, כדי שחודש חריג אחד לא ישנה את התמונה.',
  };
};
W.realend = () => {
  const s = safeToSpend(), u = s.usualRest;
  return {
    t: 'אם תוציא כרגיל', v: s.end - u,
    d: 'היתרה בסוף התקופה אם בשאר הימים תוציא כמו בדרך כלל.',
    rows: [{ k: 'צפי לסוף התקופה', v: s.end }, { k: 'פחות הוצאה רגילה בשאר התקופה', v: -u, op: '-' }, { k: 'יתרה משוערת', v: s.end - u, tot: 1 },
      { k: 'היעד', v: s.goal }, { k: 'הפרש מהיעד', txt: signed(s.end - u - s.goal), tot: 1 }],
  };
};
W.surplus = () => {
  const M = monthlyParts(), P = exitPlan();
  const surplus = P ? P.surplus : Math.round(M.inc - M.exp - M.card - M.vexp);
  return {
    t: 'עודף בחודש רגיל', v: surplus,
    d: M.strict ? 'כמה נשאר בחודש רגיל אחרי כל ההוצאות, כולל הוצאות שוטפות מהעו"ש (מצב מחמיר). חיוב שנתי נספר כחלק ה-12 שלו.'
      : 'כמה נשאר בחודש רגיל אחרי ההכנסות, החיובים הקבועים וחיוב הכרטיס (מצב רגיל). חיוב שנתי נספר כחלק ה-12 שלו.'
        + (M.vexpAll ? ' שימו לב: בדרך כלל יוצאים עוד כ-' + fMoney(M.vexpAll) + ' בחודש מהעו"ש (ביט, מזומן), שלא נכללים כאן. במצב מחמיר הם כן נכללים.' : ''),
    rows: [
      { k: 'הכנסות קבועות', v: M.inc }, { k: 'חיובים קבועים מהעו"ש (בלי חיסכון ובלי חיוב הכרטיס)', v: M.exp, op: '-' },
      { k: 'חיוב כרטיס ' + (M.fe.amount ? 'טיפוסי (חציון 3 תקופות)' : 'כמו בתקופה הנוכחית'), v: M.card, op: '-' },
      M.strict
        ? { k: M.vexp ? 'הוצאות שוטפות מהעו"ש: ביט, מזומן, חד-פעמיות (חציון 3 תקופות)' : 'הוצאות שוטפות מהעו"ש (עוד אין 2 תקופות קודמות)', v: M.vexp, op: '-' }
        : { k: 'הוצאות שוטפות מהעו"ש: לא נכללות (מצב רגיל)', txt: '—' },
      { k: 'עודף בחודש', v: surplus, tot: 1 },
    ].concat(P && P.eta ? [{ k: 'נשאר לסגור ÷ עודף', txt: Math.round(P.etaMonths * 10) / 10 + ' חודשים' }, { k: 'צפי להגעה ליעד', txt: wDYY(P.eta), tot: 1 }] : []),
  };
};
W.fut = (_, ctx) => {
  const FP = futurePeriodForecast(ctx.viewKey);
  return {
    t: 'תחזית לתקופה ' + wD(FP.start) + ' עד ' + wD(FP.end), v: FP.close,
    d: 'היתרה הצפויה בסוף התקופה. אותו חישוב כמו במסך התזרים.',
    rows: [
      { k: 'יתרה צפויה ב-' + wD(new Date(FP.start.getTime() - 864e5)) + ' (סוף התקופה הקודמת)', v: FP.open },
      { k: 'הכנסות צפויות', v: FP.inc, op: '+' }, { k: 'חיובים צפויים בעו"ש', v: FP.exp, op: '-' },
      { k: 'סגירה', v: FP.close, tot: 1 }, { k: 'השפל, ב-' + wD(FP.lowDate), v: FP.low },
    ],
    list: FP.items.map((x) => ({ d: wD(x.date), n: x.name, v: x.type === 'income' ? x.amount : -x.amount, s: x.card ? (x.est ? 'הערכה לפי תקופות קודמות' : 'סכום האשראי בתקופה') : x.type === 'income' ? 'הכנסה קבועה' : 'חיוב קבוע' })),
    lt: 'מה צפוי בתקופה',
    note: 'יתרת הפתיחה היא תוצאת התחזית מהיום ועד תחילת התקופה. "הוצאות" כאן הן כסף שיוצא מהעו"ש: חיוב הכרטיס נספר כחיוב אחד ביום החיוב, ולא כל קנייה בנפרד. ' + PROJ_NOTE,
  };
};

/* ══════ מסך התזרים: אותו טווח, אותו תרחיש פסימי ואותה סימולציה כמו במסך ══════ */
const fproj = (ctx, days) => project(days || ctx.days, { stress: ctx.stress, sim: ctx.sim });
function flags(ctx) {
  const n = [];
  if (ctx.stress) n.push('תרחיש פסימי פעיל: כל ההוצאות בתחזית הוגדלו ב-15%.');
  if (Object.keys(ctx.sim || {}).length) n.push('סימולציה פעילה: חלק מהחיובים הוזזו ליום אחר.');
  return n.length ? n.join(' ') + ' ' : '';
}
const famt = (ctx, v) => (ctx.stress && v.type !== 'income' ? v.amount * STRESS : v.amount);
/** "בחשבון היום", כמו במסך הבית: מה שנסרק, ועוד מה שכבר קרה ועוד לא נסרק.
    בתחזית המועדים שעברו נכנסים ביום הראשון, ולכן כאן הם בתוך נקודת ההתחלה ולא ברשימה */
function startOf(ctx, p) {
  let b = balToday().amount;
  ((p[0] && p[0].events) || []).forEach((v) => { if (v.late) b += (v.type === 'income' ? 1 : -1) * famt(ctx, v); });
  return b;
}
/** רשימת תנועות עם היתרה אחרי כל אחת. מתחילה מ"בחשבון היום" */
function flowItems(ctx, p, fromISO, toISO) {
  let bal = startOf(ctx, p);
  const out = [];
  p.forEach((x) => x.events.forEach((v) => {
    if (v.late) return;
    const a = famt(ctx, v), inc = v.type === 'income';
    bal += inc ? a : -a;
    if ((fromISO && x.key < fromISO) || (toISO && x.key > toISO)) return;
    out.push({
      d: wD(x.date), n: v.name, v: inc ? a : -a,
      s: (v.card ? (v.estimated ? 'הערכה' : v.cardSrc === 'manual' ? 'סכום ידני' : 'אשראי בתקופה') : v.variable ? 'הערכה לפי תקופות קודמות' : v.real ? 'כבר רשום' : v.commit ? 'תשלום ידוע' : inc ? 'הכנסה קבועה' : 'חיוב קבוע') + ' · יתרה אחרי ' + fMoney(bal),
    });
  }));
  return out;
}
const fsum = (L, sign) => L.reduce((s, x) => s + ((sign > 0 ? x.v > 0 : x.v < 0) ? Math.abs(x.v) : 0), 0);
const CARD_NOTE = 'חיוב הכרטיס הקרוב הוא סך עסקאות האשראי שנרשמו בתקופה הנוכחית, ועוד חיובים קבועים בכרטיס שעוד יגיעו עד סוף התקופה. ' +
  'בתקופות הבאות הוא מוערך לפי חציון 3 התקופות הסגורות האחרונות. לחיצה על שורת חיוב הכרטיס מפרטת אותו.';
const lowOf = (p) => p.reduce((low, x) => (x.balance < low.balance ? x : low), p[0]);

W.flow_low = (days, ctx) => {
  days = Number(days) || ctx.days;
  const p = fproj(ctx, days), now = startOf(ctx, p), low = lowOf(p);
  const L = flowItems(ctx, p, null, low.key), lim = Number(S.set.odLimit) || 0;
  return {
    t: 'נקודת השפל ' + phraseForDays(days), v: low.balance,
    d: 'היתרה הכי נמוכה שהעו"ש יגיע אליה בטווח שנבחר, לפי התחזית. זה היום שבו כדאי להיזהר, לא סוף החודש.',
    rows: [
      { k: 'בחשבון היום', v: now },
      { k: 'הכנסות צפויות עד ' + wD(low.date) + ' (' + L.filter((x) => x.v > 0).length + ')', v: fsum(L, 1), op: '+' },
      { k: 'חיובים צפויים עד ' + wD(low.date) + ' (' + L.filter((x) => x.v < 0).length + ')', v: fsum(L, -1), op: '-' },
      { k: 'השפל, ב-' + wD(low.date), v: low.balance, tot: 1 },
    ].concat(lim ? [{ k: 'המסגרת', v: -lim }, { k: low.balance < -lim ? 'חריגה מהמסגרת' : 'מרחק מהמסגרת', txt: fMoney(Math.abs(low.balance + lim)), tot: 1 }] : []),
    list: L, lt: 'התנועות עד השפל', note: flags(ctx) + flowNote(),
  };
};
W.flow_end = (_, ctx) => {
  const p = fproj(ctx), now = startOf(ctx, p), last = p[p.length - 1], L = flowItems(ctx, p, null, null);
  return {
    t: 'יתרה ב-' + wD(last.date), v: last.balance,
    d: 'היתרה הצפויה בסוף הטווח שנבחר, אם לא תהיה אף הוצאה מעבר למה שבתחזית.',
    rows: [{ k: 'בחשבון היום', v: now }, { k: 'הכנסות צפויות (' + L.filter((x) => x.v > 0).length + ')', v: fsum(L, 1), op: '+' },
      { k: 'חיובים צפויים (' + L.filter((x) => x.v < 0).length + ')', v: fsum(L, -1), op: '-' }, { k: 'בסוף הטווח', v: last.balance, tot: 1 }],
    list: L, lt: 'כל התנועות בטווח',
    note: flags(ctx) + flowNote() + ' ' + CARD_NOTE,
  };
};
W.flow_limit = (_, ctx) => {
  const p = fproj(ctx), low = lowOf(p), lim = Number(S.set.odLimit) || 0;
  return {
    t: 'השפל מול המסגרת', v: null,
    d: 'כמה מהמסגרת בעו"ש תנוצל ביום הכי נמוך בתחזית.',
    rows: [{ k: 'השפל (' + wD(low.date) + ')', v: low.balance }, { k: 'המסגרת', v: lim, op: '÷' },
      { k: 'ניצול בשפל', txt: Math.round((Math.max(0, -low.balance) / lim) * 100) + '%', tot: 1 }]
      .concat(low.balance < -lim ? [{ k: 'חריגה (השפל פחות המסגרת)', txt: fMoney(-lim - low.balance), tot: 1 }] : []),
    note: 'ריבית המינוס מחושבת לפי היתרה בכל יום, ולכן היום הכי נמוך הוא החשוב. את המסגרת משנים בהגדרות התזרים למטה.',
  };
};
W.flow_grp = (key, ctx) => {
  const p = fproj(ctx), now = startOf(ctx, p), ps = fISO(periodStart(key)), pend = fISO(periodEnd(key));
  let open = now, last = null, low = null;
  p.forEach((x) => {
    if (x.key < ps) { open = x.balance; return; }
    if (x.key > pend) return;
    last = x;
    if (!low || x.balance < low.balance) low = x;
  });
  const L = flowItems(ctx, p, ps, pend), first = ps > todayISO();
  return {
    t: 'תקופה ' + periodShort(key), v: last ? last.balance : open,
    d: 'מה צפוי לזוז בעו"ש בתקופה הזו, ואיך היתרה תיראה בסופה.' + (p.length && !first ? ' התקופה כבר התחילה, ולכן החישוב מתחיל מהיום.' : ''),
    rows: [{ k: first ? 'יתרה צפויה בתחילת התקופה' : 'בחשבון היום', v: open }, { k: 'הכנסות צפויות', v: fsum(L, 1), op: '+' },
      { k: 'חיובים צפויים', v: fsum(L, -1), op: '-' }, { k: 'בסוף התקופה (' + wD(periodEnd(key)) + ')', v: last ? last.balance : open, tot: 1 }]
      .concat(low ? [{ k: 'השפל בתקופה (' + wD(low.date) + ')', v: low.balance }] : []),
    list: L, lt: 'התנועות בתקופה', note: flags(ctx) + flowNote() + ' ' + CARD_NOTE,
  };
};
W.flow_day = (iso, ctx) => {
  const p = fproj(ctx);
  let prev = startOf(ctx, p), day = null;
  for (const x of p) { if (x.key === iso) { day = x; break; } prev = x.balance; }
  if (!day) return null;
  const rows = [{ k: day === p[0] ? 'בחשבון היום' : 'יתרה לפני ' + wD(day.date), v: prev }];
  day.events.forEach((v) => { if (!v.late) rows.push({ k: v.name, v: famt(ctx, v), op: v.type === 'income' ? '+' : '-' }); });
  rows.push({ k: 'יתרה אחרי ' + wD(day.date), v: day.balance, tot: 1 });
  return { t: 'יתרה אחרי ' + wD(day.date), v: day.balance, d: 'היתרה בסוף היום, אחרי כל מה שצפוי לרדת ולהיכנס בו.', rows, note: flags(ctx) };
};
W.flow_card = (iso) => {
  const lc = cardLateCharge();
  if (lc && iso === lc.iso) {
    const txs = filterActive(S.tx.filter((t) => t.month === lc.key && getTxSrc(t) === 'credit' && (t.type === 'expense' || t.type === 'income')));
    return {
      t: 'חיוב כרטיס אשראי של ' + wD(lc.iso) + ' (טרם נקלט)', v: lc.amount,
      d: 'התאריך של החיוב עבר, אבל הוא עוד לא מופיע בעו"ש: העו"ש לא נסרק מאז, והיתרה שהזנת היא מלפני החיוב. לכן הוא נספר בתזרים מחר, כדי שהיתרה הצפויה לא תהיה גבוהה מדי.',
      rows: [{ k: 'עסקאות אשראי בתקופה ' + periodShort(lc.key), v: lc.amount, tot: 1 }],
      list: txs.map(wTx).sort(byAmt).slice(0, 15), lt: 'העסקאות הגדולות',
      note: 'ברגע שתסרוק את העו"ש ותופיע שורת החיוב של הכרטיס (ישראכרט, כאל, מקס וכו\'), הוא ייצא מכאן.',
    };
  }
  const ev = iso ? cardChargeEvents(new Date(todayISO() + 'T12:00:00'), 400)[iso] : null;
  const c = cardChargeAmount(), d = iso ? ' ב-' + wD(iso) : '';
  if (ev && ev.cardSrc === 'copy') return {
    t: 'חיוב כרטיס אשראי' + d + ' (הערכה)', v: ev.amount,
    d: 'העסקאות שיחויבו בתאריך הזה עוד לא קרו. כדי להעריך אותן צריך דוחות של חודשים קודמים, ועדיין לא נסרקו. בינתיים מניחים חיוב בגובה החיוב הקרוב.',
    rows: [{ k: 'החיוב הקרוב (' + periodShort(cardCurKey()) + ')', v: c ? c.amount : ev.amount }, { k: 'הערכה לחיוב הזה', v: ev.amount, tot: 1 }],
    note: 'אחרי סריקת דוחות של חודשים קודמים, ההערכה תהיה חציון של שלוש התקופות האחרונות.',
  };
  if (ev && ev.estimated) {
    const sums = cardPeriodSums(), keys = cardClosedKeys(sums), E = cardEstimateFor(keyOfISO(iso));
    const rows = keys.map((k) => ({ k: 'אשראי בתקופה ' + periodShort(k), v: sums[k] })).concat([{ k: 'חציון (' + keys.length + ' תקופות)', v: E.base, tot: 1 }]);
    if (E.withDue) {
      rows.push({ k: 'חציון בלי עסקאות בתשלומים', v: E.plain });
      E.dueList.forEach((x) => rows.push({ k: x.name, v: x.amount, op: '+' }));
      rows.push({ k: 'הערכה לחיוב הזה', v: E.amount, tot: 1 });
    }
    return {
      t: 'חיוב כרטיס אשראי' + d + ' (הערכה)', v: ev.amount,
      d: 'העסקאות שיחויבו בתאריך הזה עוד לא קרו, ולכן הסכום מוערך לפי התקופות הקודמות.' +
        (E.withDue ? ' התשלומים הידועים לחיוב הזה גבוהים מבעבר, ולכן הם נוספים לחציון של שאר העסקאות.' : ''),
      rows,
      note: 'חציון ולא ממוצע, כדי שחודש חריג אחד לא ישפיע. אם יש פחות משלוש תקופות סגורות, ההערכה פחות מדויקת.',
    };
  }
  if (c && c.src === 'manual') return {
    t: 'חיוב כרטיס אשראי' + d, v: c.amount, d: 'הזנת סכום ידני לחיוב הקרוב, ולכן התחזית משתמשת בו במקום בסכום העסקאות.',
    rows: [{ k: 'סכום ידני', v: c.amount, tot: 1 }, { k: 'לשם השוואה: עסקאות אשראי שנרשמו', v: cardPeriodCredit().amount }],
    note: 'הסכום הידני נשאר עד שתבטל אותו, בחלק "חיוב כרטיס אשראי" במסך התזרים. כדאי לבטל אחרי שהחיוב ירד.',
  };
  if (!c) return { t: 'חיוב כרטיס אשראי', v: 0, d: 'אין עדיין עסקאות אשראי בתקופה הנוכחית.' };
  const k = cardCurKey();
  const txs = filterActive(S.tx.filter((t) => t.month === k && getTxSrc(t) === 'credit' && (t.type === 'expense' || t.type === 'income')));
  const later = iso && keyOfISO(iso) > k;
  let exp = 0, ref = 0, ne = 0, nr = 0;
  txs.forEach((t) => { const a = absAmt(t); if (t.type === 'income') { ref += a; nr++; } else { exp += a; ne++; } });
  const rows = [{ k: 'עסקאות אשראי בתקופה ' + periodShort(k) + ' (' + ne + ')', v: exp }];
  if (ref) rows.push({ k: 'זיכויים והחזרים (' + nr + ')', v: ref, op: '-' });
  (c.parts || []).forEach((x) => rows.push({ k: x.name + (x.st === 'due' ? ' (יגיע ב-' : ' (צפוי היה ב-') + wD(x.date) + ')', v: x.amount, op: '+' }));
  rows.push({ k: 'חיוב הכרטיס', v: c.amount, tot: 1 });
  return {
    t: 'חיוב כרטיס אשראי' + d, v: c.amount,
    d: 'כל עסקאות האשראי בתקופה ' + periodShort(k) + ' יורדות מהעו"ש יחד, ביום אחד. הסכום זהה ל"הוצאות לפי מקור: אשראי" במסך הבית.',
    rows, list: txs.map(wTx).sort(byAmt).slice(0, 20), lt: 'העסקאות הגדולות',
    note: (later ? 'זה חיוב של חודש מאוחר יותר. אין עדיין 3 תקופות אשראי סגורות להערכה, ולכן הוא מוערך כמו החיוב הקרוב. ' : '') +
      ((c.parts || []).length ? 'חיובים קבועים בכרטיס ותשלומים שעוד לא נמצאו בעסקאות נוספו לחיוב. ' : '') +
      'עסקה חדשה באשראי עד ' + wD(periodEnd(k)) + ' תגדיל את החיוב. אם הסכום לא תואם את הדוח, אפשר להזין סכום ידני.',
  };
};
W.flow_cards = (_, ctx) => {
  const pr = project(ctx.days, { sim: ctx.sim });
  const L = [];
  let tot = 0;
  pr.forEach((x) => x.events.forEach((v) => { if (v.card && !v.late) { tot += v.amount; L.push({ k: wDY(x.date) + (v.estimated ? ' · הערכה' : ' · העסקאות שנרשמו בתקופה'), v: v.amount, op: '+' }); } }));
  L.push({ k: 'סה"כ ' + rangePhrase(ctx.per), v: tot, tot: 1 });
  const fe = cardFutureEstimate();
  return {
    t: 'חיובי כרטיס אשראי ' + rangePhrase(ctx.per), v: tot,
    d: 'כל חיובי הכרטיס שיורדים מהעו"ש בטווח שנבחר, חיוב אחד בכל תקופה. לחיצה על חיוב בתזרים מפרטת אותו.', rows: L,
    note: 'החיוב הקרוב הוא סך העסקאות שכבר נרשמו בתקופה. החיובים שאחריו הם הערכה: ' + (fe.amount ? 'חציון ' + fe.n + ' תקופות סגורות.' : 'כמו החיוב הקרוב, כי עוד לא נסרקו חודשים קודמים.'),
  };
};
function recWhy(kind, ctx) {
  const L = S.rec.filter((r) => (kind === 'income' ? r.type === 'income' : r.type !== 'income' && (r.source || 'cash') !== 'credit'));
  const RS = recInRange(ctx.days, { sim: ctx.sim });
  let tot = 0;
  const rows = [], skipped = [];
  L.forEach((r) => {
    const x = RS[r.id];
    if (x && x.sum) x.items.forEach((it) => { tot += it.amount; rows.push({ k: r.description + ' · ' + wD(it.d), v: it.amount, op: '+' }); });
    else {
      const o = recNow(r), paid = o && o.st === 'paid';
      skipped.push({ d: paid ? wD(o.t.date) : '', n: r.description, v: (kind === 'income' ? 1 : -1) * (paid ? o.amount : 0),
        s: paid ? 'כבר ' + (kind === 'income' ? 'נכנס' : 'ירד') + ', בתוך היתרה של היום' : 'המועד הבא אחרי סוף הטווח' });
    }
  });
  rows.push({ k: 'סה"כ ' + rangePhrase(ctx.per), v: tot, tot: 1 });
  return {
    t: kind === 'income' ? 'הכנסות קבועות בתחזית' : 'חיובים קבועים מהעו"ש בתחזית', v: tot,
    d: 'כמה ' + (kind === 'income' ? 'עוד ייכנס' : 'עוד ייצא') + ' מהעו"ש ' + rangePhrase(ctx.per) + ' (' + wDY(rangeEnd(ctx.per)) + '), לפי החיובים הקבועים. זה בדיוק מה שהתזרים ' + (kind === 'income' ? 'מוסיף' : 'מוריד') + '.',
    rows, list: skipped, lt: 'לא נספרים בטווח',
    note: 'חיוב שכבר ' + (kind === 'income' ? 'נכנס' : 'ירד') + ' לא נספר שוב, כי הוא כבר בתוך היתרה של היום. גם חיוב שהתאריך שלו עבר ועוד לא נקלט בסריקה כבר בתוך "יתרה היום". ' +
      'הסכום של חיוב משתנה הוא חציון שלושת החיובים האחרונים שזוהו.',
  };
}
W.flow_inc = (_, ctx) => recWhy('income', ctx);
W.flow_cash = (_, ctx) => recWhy('cash', ctx);
W.flow_cpace = () => {
  const pc = cardPace();
  if (!pc) return null;
  const full = cardPeriodSums(), keys = cardClosedKeys(full), part = cardPeriodSums(pc.day);
  return {
    t: 'קצב האשראי', v: pc.soFar,
    d: 'כמה נרשם באשראי מתחילת התקופה ועד היום, מול כמה בדרך כלל נרשם עד אותו יום בתקופה.',
    rows: [{ k: 'עד היום (יום ' + pc.day + ' מתוך ' + pc.len + ')', v: pc.soFar }]
      .concat(keys.map((k) => ({ k: 'עד יום ' + pc.day + ' בתקופה ' + periodShort(k), v: part[k] || 0 })))
      .concat([{ k: 'בדרך כלל עד היום (חציון)', v: pc.usual, tot: 1 }, { k: 'הפרש', txt: (pc.pct > 0 ? '+' : '') + pc.pct + '%', tot: 1 },
        { k: 'תקופה שלמה בדרך כלל', v: pc.usualFull }, { k: 'מה שנשאר בדרך כלל עד הסוף', v: Math.max(0, pc.usualFull - pc.usual), op: '+' },
        { k: 'צפי לחיוב (עד היום + מה שנשאר)', v: pc.proj, tot: 1 }]),
    note: 'הצפי מניח שבשאר התקופה תוציא כמו בדרך כלל. התחזית עצמה לא משתמשת בצפי הזה, אלא רק במה שכבר נרשם.',
  };
};

/* ══════ חיסכון ══════ */
W.buf = () => {
  // אותו חישוב כמו bufSuggest: מה שיוצא מהעו"ש בחודש רגיל
  const M = monthlyParts(), tpl = cardTemplate(), rows = [];
  S.rec.forEach((r) => {
    if (r.type === 'income' || (r.source || 'cash') === 'credit' || isSavingRec(r) || (M.card > 0 && r === tpl)) return;
    rows.push({ k: r.description, v: recEst(r) / recEvery(r), op: '+' });
  });
  rows.push({ k: 'חיוב כרטיס ' + (M.fe.amount ? 'טיפוסי (חציון)' : 'כמו בתקופה הנוכחית'), v: M.card, op: '+' });
  if (M.vexp) rows.push({ k: 'הוצאות שוטפות מהעו"ש (חציון)', v: M.vexp, op: '+' });
  rows.push({ k: 'חודש של יציאות מהעו"ש', v: M.exp + M.card + M.vexp, tot: 1 });
  rows.push({ k: 'מעוגל ל-500 (ההמלצה)', v: bufSuggest(), tot: 1 });
  if (S.set.bufTarget > 0) rows.push({ k: 'הכרית שבחרת', v: S.set.bufTarget, tot: 1 });
  return {
    t: 'כרית ביטחון', v: S.set.bufTarget > 0 ? S.set.bufTarget : bufSuggest(),
    d: 'הסכום שצריך להישאר בעו"ש גם ביום הכי נמוך בחודש. כך הוצאה לא צפויה לא מחזירה למינוס. ההמלצה היא חודש אחד של יציאות מהעו"ש.',
    rows, note: 'הכרית נמדדת מול השפל, לא מול היתרה היום: אם ביום הכי נמוך, לפני המשכורת, יש בעו"ש לפחות את הכרית, השלב הושלם.',
  };
};
W.sav_month = (which) => {
  const SP = savPeriod(), k = curKey();
  which = which || 'got';
  const V = { done: SP.done, got: SP.done + SP.planned, target: SP.M, left: SP.left }[which];
  const T = { done: 'הועבר לחיסכון בתקופה', got: 'הועבר או מתוכנן לחיסכון בתקופה', target: 'יעד החיסכון לתקופה', left: 'חיסכון שעוד לא הועבר בתקופה' }[which];
  const rows = SP.goals.filter((s) => s.monthly > 0).map((s) => ({ k: s.g.emoji + ' ' + s.g.name + (s.monthlySrc === 'deadline' ? ' (חסר ÷ חודשים עד היעד)' : ' (סכום קבוע)'), v: s.monthly, op: '+' }));
  rows.push({ k: 'יעד החיסכון לחודש', v: SP.M, tot: 1 }, { k: 'כבר הועבר בתקופה', v: SP.done },
    { k: 'מתוכנן עד ' + wD(periodEnd(k)) + ' (הוראת קבע)', v: SP.planned, op: '+' }, { k: 'נשאר להעביר', v: SP.left, tot: 1 });
  return {
    t: T + ' (' + periodShort(k) + ')', v: V,
    d: 'כמה כסף עבר מהעו"ש לחיסכון בתקופה הזו, מול מה שהיעדים דורשים בחודש.',
    rows, list: SP.list.map(wTx), lt: 'העברות לחיסכון בתקופה',
    note: 'העברה לחיסכון מזוהה לפי התיאור שלה בדף הבנק (פיקדון, חיסכון, קופת גמל, או מילים שהגדרת ליעד). היא לא נספרת כהוצאה, אבל כן מורידה מהעו"ש. הסכום שנשאר להעביר כבר שמור בצד ב"אפשר להוציא".',
  };
};
W.sav_left = () => W.sav_month('left');
W.sav_total = () => {
  const SP = savPeriod();
  let tot = 0;
  const rows = SP.goals.map((s) => { tot += s.total; return { k: s.g.emoji + ' ' + s.g.name, v: s.total, op: '+' }; });
  rows.push({ k: 'סה"כ', v: tot, tot: 1 });
  const un = savUnassigned();
  return {
    t: 'נחסך בכל היעדים', v: tot, d: 'סכום מה שיש בכל יעדי החיסכון.', rows,
    note: un.length ? un.length + ' העברות לחיסכון עוד לא שויכו ליעד ולא נכללות כאן. אפשר לשייך אותן בתחתית המסך.' : '',
  };
};
W.sav_goal = (id) => {
  const g = S.savings.find((x) => String(x.id) === String(id));
  if (!g) return null;
  const s = savGoalStats(g);
  return {
    t: g.emoji + ' ' + g.name, v: s.total,
    d: 'כמה יש ביעד היום. מחושב מהסכום שהיה בו כשהתחלת, ועוד ההעברות מהעו"ש שזוהו כשייכות אליו.',
    rows: [{ k: 'סכום התחלתי והוספות ידניות', v: s.base }, { k: 'העברות לחיסכון מ-' + wD(savFrom(g)), v: s.dep, op: '+' },
      { k: 'משיכות מהחיסכון', v: s.wd, op: '-' }, { k: 'יש ביעד', v: s.total, tot: 1 }, { k: 'היעד', v: s.target }, { k: 'חסר', v: s.rem, tot: 1 }],
    list: s.list.slice().sort((a, b) => String(b.date).localeCompare(String(a.date))).map(wTx), lt: 'ההעברות ששויכו',
    note: s.kw.length ? 'העברה משויכת ליעד אם התיאור שלה בדף הבנק מכיל אחת מהמילים: ' + s.kw.join(', ') + '. אפשר לשנות בעריכת היעד.' : 'ליעד אין מילים לזיהוי, ולכן רק הוספה ידנית מזיזה אותו.',
  };
};
W.sav_req = (id) => {
  const g = S.savings.find((x) => String(x.id) === String(id));
  if (!g) return null;
  const s = savGoalStats(g);
  if (s.monthlySrc === 'fixed') return {
    t: 'חיסכון חודשי ליעד', v: s.monthly, d: 'סכום חודשי קבוע שהגדרת ליעד.',
    rows: [{ k: 'סכום חודשי', v: s.monthly, tot: 1 }, { k: 'חסר ליעד', v: s.rem }].concat(s.eta ? [{ k: 'צפי להשגה', txt: wDYY(s.eta), tot: 1 }] : []),
  };
  const dl = String(g.deadline).slice(0, 10), m = savMonthsTo(dl);
  return {
    t: 'חיסכון חודשי ליעד', v: s.monthly, d: 'כמה צריך להעביר בכל חודש כדי להגיע ליעד בתאריך שקבעת.',
    rows: [{ k: 'חסר ליעד בתחילת התקופה', v: s.remStart }, { k: 'חודשים עד ' + wD(dl), txt: String(Math.round(m * 10) / 10), op: '÷' }, { k: 'בחודש', v: s.monthly, tot: 1 }]
      .concat(s.avg ? [{ k: 'בפועל, ממוצע 3 החודשים האחרונים', v: s.avg }] : []),
    note: s.avg && s.avg < s.monthly ? 'בקצב של היום היעד לא יושג בזמן. אפשר להעלות את ההעברה החודשית או לדחות את התאריך.' : '',
  };
};

/* הסברים עם ממשק מותאם (בחירת עסקה, תיקון שמות) */
W.rec = (id) => ({ kind: 'rec', id });
W.revfix = () => ({ kind: 'revfix' });
W.recmiss = () => ({ kind: 'recmiss' });

export function cardChargeTxt(BF) {
  return BF.cards.map((c) => 'חיוב כרטיס אשראי (' + wD(c.iso) + ')').join(', ');
}

/** @param ctx { viewKey, days, per, stress, sim } מצב המסך */
export function explain(key, arg, ctx) {
  const f = W[key];
  if (!f) return null;
  try {
    return f(arg, ctx);
  } catch (e) {
    console.error('explain', key, e);
    return null;
  }
}

